/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import * as React from "react";
import { ThemeType } from "../DesignSystem/Theme/Theme";
import { FaFolderOpen } from "react-icons/fa";
import { loggedNotify, Component } from "../Shared/Notify";

interface QuickFormDropZoneProps {
  theme: ThemeType;
  onFilesSelected: (files: File[]) => void;
  axiosContext: any;
}

const ACCEPTED_FILE_TYPES = [".zip", ".tar.gz", ".xml", ".proto"];
const MAX_FILE_SIZE_MB = 10;

export const QuickFormDropZone: React.FC<QuickFormDropZoneProps> = ({
  theme,
  onFilesSelected,
  axiosContext,
}) => {
  const [isDragActive, setIsDragActive] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const validateFile = (file: File): { valid: boolean; error?: string } => {
    // Check extension
    const fileName = file.name.toLowerCase();
    const hasValidExtension = ACCEPTED_FILE_TYPES.some((ext) =>
      fileName.endsWith(ext)
    );

    if (!hasValidExtension) {
      return {
        valid: false,
        error: `${
          file.name
        }: Invalid file type. Only ${ACCEPTED_FILE_TYPES.join(
          ", "
        )} files are accepted.`,
      };
    }

    // Check size
    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > MAX_FILE_SIZE_MB) {
      return {
        valid: false,
        error: `${file.name}: File size (${fileSizeMB.toFixed(
          1
        )}MB) exceeds ${MAX_FILE_SIZE_MB}MB limit.`,
      };
    }

    return { valid: true };
  };

  const validateAndFilterFiles = (files: File[]): File[] => {
    const validFiles: File[] = [];
    const errors: string[] = [];

    files.forEach((file) => {
      const validation = validateFile(file);
      if (validation.valid) {
        validFiles.push(file);
      } else if (validation.error) {
        errors.push(validation.error);
      }
    });

    // Show errors
    if (errors.length > 0) {
      errors.forEach((error) =>
        loggedNotify.negative(axiosContext, Component.QUICKFORM, error)
      );
    }

    return validFiles;
  };

  const handleDragEnter = (e: React.DragEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  };

  const handleDragOver = (e: React.DragEvent): void => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      const validFiles = validateAndFilterFiles(files);
      if (validFiles.length > 0) {
        onFilesSelected(validFiles);
      }
    }
  };

  const handleFileInputChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    if (files.length > 0) {
      const validFiles = validateAndFilterFiles(files);
      if (validFiles.length > 0) {
        onFilesSelected(validFiles);
      }
    }
    // Reset input to allow selecting the same files again
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleBrowseClick = (): void => {
    fileInputRef.current?.click();
  };

  return (
    <div
      style={{
        margin: `${theme.spacing.lg} 30px ${theme.spacing.md} 30px`,
        border: `2px dashed ${
          isDragActive
            ? theme.colors.searchMainColor
            : theme.colors.searchBorderColor
        }`,
        borderRadius: "8px",
        padding: "24px 24px",
        textAlign: "center",
        background: isDragActive
          ? theme.colors.backgroundTertiary
          : theme.colors.dropdownBackground,
        transition: "all 0.3s ease",
        cursor: "pointer",
        transform: isDragActive ? "scale(1.02)" : "scale(1)",
      }}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onClick={handleBrowseClick}
    >
      <div
        style={{
          marginBottom: theme.spacing.sm,
        }}
      >
        <FaFolderOpen
          size={36}
          style={{
            color: theme.colors.secondary,
            opacity: 0.6,
          }}
        />
      </div>
      <h3
        style={{
          fontSize: theme.fontSizes.large,
          fontWeight: 600,
          marginBottom: "4px",
          color: theme.colors.text,
          fontFamily: theme.fonts.primary,
        }}
      >
        Drop files here
      </h3>
      <p
        style={{
          fontSize: theme.fontSizes.medium,
          color: theme.colors.secondaryTextColor,
          marginBottom: theme.spacing.sm,
          fontFamily: theme.fonts.primary,
        }}
      >
        Or click to browse and select multiple files
      </p>

      {/* File restrictions info */}
      <div
        style={{
          marginBottom: theme.spacing.sm,
          padding: 0,
        }}
      >
        <p
          style={{
            fontFamily: theme.fonts.primary,
            color: theme.colors.secondaryTextColor,
            fontSize: theme.fontSizes.small,
            textAlign: "center",
            margin: 0,
            marginBottom: "2px",
          }}
        >
          Accepted file types: {ACCEPTED_FILE_TYPES.join(", ")}
        </p>
        <p
          style={{
            fontFamily: theme.fonts.primary,
            color: theme.colors.secondaryTextColor,
            fontSize: theme.fontSizes.small,
            textAlign: "center",
            margin: 0,
          }}
        >
          Maximum file upload size: {MAX_FILE_SIZE_MB}MB
        </p>
      </div>

      <button
        style={{
          background: theme.colors.buttonPrimaryColor,
          color: theme.colors.primaryElementFontColor,
          border: "none",
          padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
          borderRadius: "6px",
          fontSize: theme.fontSizes.medium,
          fontWeight: 500,
          cursor: "pointer",
          transition: "background 0.2s",
          fontFamily: theme.fonts.primary,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background =
            theme.colors.buttonPrimaryHoverColor;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = theme.colors.buttonPrimaryColor;
        }}
        onClick={(e) => {
          e.stopPropagation();
          handleBrowseClick();
        }}
      >
        Browse Files
      </button>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        onChange={handleFileInputChange}
        style={{ display: "none" }}
        accept=".zip,.tar.gz,.xml,.proto"
      />
    </div>
  );
};
